import { useCallback } from 'react';
import { toast } from 'sonner';
import { useMutation } from '@apollo/client/react';

import { ClientError, getApiError } from 'src/utils/errors';
import {
  ActivityNotificationEnum,
  EscrowPartyTypeEnum,
  DisruptionNotificationEnum,
  DisruptionNotificationGracePeriodEnum,
  type IEscrowUpdateDepositorNotificationsParams,
  type IEscrowUpdateBeneficiaryNotificationsParams,
} from 'src/graphql/schema';
import { EscrowUpdateBeneficiaryNotificationsDocument } from 'src/graphql/actions/escrow/escrowUpdateBeneficiaryNotifications.mutation';
import { EscrowUpdateDepositorNotificationsDocument } from 'src/graphql/actions/escrow/escrowUpdateDepositorNotifications.mutation';

export const useEscrowNotificationHandlers = ({
  escrowId,
  currentCompanyRole,
}: {
  escrowId: string;
  currentCompanyRole: EscrowPartyTypeEnum;
}) => {
  const [updateDepositorNotifications] = useMutation(EscrowUpdateDepositorNotificationsDocument);
  const [updateBeneficiaryNotifications] = useMutation(EscrowUpdateBeneficiaryNotificationsDocument);

  const handleMutationResponse = useCallback((success: boolean | undefined, errors: string[] | null | undefined) => {
    if (success) {
      toast.success('Saved!');
    } else if (errors?.length) {
      throw new ClientError(errors[0]);
    } else {
      throw new ClientError('Unknown error occurred');
    }
  }, []);

  const onDepositorNotificationsChange = useCallback(
    async (escrowParams: IEscrowUpdateDepositorNotificationsParams) => {
      try {
        const { data } = await updateDepositorNotifications({
          variables: { escrowId, escrowParams },
        });
        handleMutationResponse(
          data?.escrowUpdateDepositorNotifications?.success,
          data?.escrowUpdateDepositorNotifications?.errors,
        );
      } catch (e: unknown) {
        getApiError(e, toast.error);
      }
    },
    [escrowId, updateDepositorNotifications, handleMutationResponse],
  );

  const onBeneficiaryNotificationsChange = useCallback(
    async (escrowParams: IEscrowUpdateBeneficiaryNotificationsParams) => {
      try {
        const { data } = await updateBeneficiaryNotifications({
          variables: { escrowId, escrowParams },
        });
        handleMutationResponse(
          data?.escrowUpdateBeneficiaryNotifications?.success,
          data?.escrowUpdateBeneficiaryNotifications?.errors,
        );
      } catch (e: unknown) {
        getApiError(e, toast.error);
      }
    },
    [escrowId, updateBeneficiaryNotifications, handleMutationResponse],
  );

  const onActivityNotificationChange = useCallback(
    (activityNotification: ActivityNotificationEnum) => {
      if (!activityNotification) return;

      if (currentCompanyRole === EscrowPartyTypeEnum.Depositor) {
        onDepositorNotificationsChange({ depositorActivityNotification: activityNotification });
      } else {
        onBeneficiaryNotificationsChange({ beneficiaryActivityNotification: activityNotification });
      }
    },
    [currentCompanyRole, onDepositorNotificationsChange, onBeneficiaryNotificationsChange],
  );

  const onDisruptionNotificationChange = useCallback(
    (value: DisruptionNotificationEnum) => {
      if (!value) return;

      const gracePeriod =
        value === DisruptionNotificationEnum.SendNotification ? DisruptionNotificationGracePeriodEnum.Immediate : null;

      if (currentCompanyRole === EscrowPartyTypeEnum.Depositor) {
        onDepositorNotificationsChange({
          depositorDisruptionNotification: value,
          depositorDisruptionNotificationGracePeriod: gracePeriod,
        });
      } else {
        onBeneficiaryNotificationsChange({
          beneficiaryDisruptionNotification: value,
          beneficiaryDisruptionNotificationGracePeriod: gracePeriod,
        });
      }
    },
    [currentCompanyRole, onDepositorNotificationsChange, onBeneficiaryNotificationsChange],
  );

  const onGracePeriodChange = useCallback(
    (gracePeriod: DisruptionNotificationGracePeriodEnum) => {
      if (!gracePeriod) return;

      if (currentCompanyRole === EscrowPartyTypeEnum.Depositor) {
        onDepositorNotificationsChange({
          depositorDisruptionNotificationGracePeriod: gracePeriod,
        });
      } else {
        onBeneficiaryNotificationsChange({
          beneficiaryDisruptionNotificationGracePeriod: gracePeriod,
        });
      }
    },
    [currentCompanyRole, onDepositorNotificationsChange, onBeneficiaryNotificationsChange],
  );

  return {
    onActivityNotificationChange,
    onDisruptionNotificationChange,
    onGracePeriodChange,
  } as const;
};
